import { Controller } from "@hotwired/stimulus"

const ITEM_SELECTOR =
  "[data-dropdown-item]:not([disabled]):not([aria-disabled='true'])"

// Connects to data-controller="ui--dropdown-menu"
//
// Hotwire parity with @a-safe-digital/react-dropdown-menu (Radix). Handles
// open/close, keyboard navigation, submenu open-on-hover/focus and
// checkbox/radio state. The panel is an absolutely positioned child of the
// `relative` root, placed by Tailwind classes derived from `align`/`side` (no
// @floating-ui/dom, no portal, so side-opening submenus are never clipped).
// On open the controller corrects that position only if the panel would cross
// a viewport edge (CONEK-8561): a top/bottom panel shifts sideways back inside,
// and a side-opening panel or submenu flips to the side with more room. A panel
// that fits is left exactly where the CSS puts it. Corrections are inline
// styles, never class names (JS-built classes have no CSS rule behind them).
export default class extends Controller {
  static targets = ["trigger", "content"]

  connect() {
    this.contentEl = this.contentTarget
    this.open = false
    this.submenuStack = []

    this.onContentClick = this.handleContentClick.bind(this)
    this.onContentKeydown = this.handleContentKeydown.bind(this)
    this.onContentOver = this.handleContentOver.bind(this)
    this.onContentLeave = this.handleContentLeave.bind(this)
    this.onDocPointerdown = this.handleDocPointerdown.bind(this)
    this.onResize = this.reposition.bind(this)
    this.onContentAnimationEnd = this.handleContentAnimationEnd.bind(this)

    this.contentEl.addEventListener("click", this.onContentClick)
    this.contentEl.addEventListener("keydown", this.onContentKeydown)
    this.contentEl.addEventListener("mouseover", this.onContentOver)
    this.contentEl.addEventListener("mouseleave", this.onContentLeave)
    this.contentEl.addEventListener("animationend", this.onContentAnimationEnd)
  }

  disconnect() {
    this.contentEl?.removeEventListener("click", this.onContentClick)
    this.contentEl?.removeEventListener("keydown", this.onContentKeydown)
    this.contentEl?.removeEventListener("mouseover", this.onContentOver)
    this.contentEl?.removeEventListener("mouseleave", this.onContentLeave)
    this.contentEl?.removeEventListener(
      "animationend",
      this.onContentAnimationEnd,
    )
    document.removeEventListener("pointerdown", this.onDocPointerdown, true)
    window.removeEventListener("resize", this.onResize)
  }

  // === top-level open/close ===

  toggle(event) {
    event?.preventDefault()
    event?.stopPropagation()
    if (this.open) {
      this.closeMenu()
    } else {
      this.openMenu()
    }
  }

  openMenu() {
    if (this.open) return
    this.open = true

    this.contentEl.classList.remove("hidden")
    this.placePanel(this.contentEl, this.triggerTarget)
    this.contentEl.dataset.state = "open"
    this.triggerButton?.setAttribute("aria-expanded", "true")

    document.addEventListener("pointerdown", this.onDocPointerdown, true)
    window.addEventListener("resize", this.onResize)
    // Focus the panel itself (not an item) so nothing is pre-highlighted on a
    // pointer-open — the highlight then follows the pointer / arrow keys.
    this.contentEl.focus()
  }

  closeMenu({ focusTrigger = true } = {}) {
    if (!this.open) return
    this.open = false

    this.closeAllSubmenus()
    this.contentEl.classList.add("hidden")
    this.contentEl.dataset.state = "closed"
    this.resetPanel(this.contentEl)
    this.triggerButton?.setAttribute("aria-expanded", "false")

    document.removeEventListener("pointerdown", this.onDocPointerdown, true)
    window.removeEventListener("resize", this.onResize)
    if (focusTrigger) this.triggerButton?.focus()
  }

  // === submenus ===

  handleContentOver(event) {
    const item = event.target.closest("[data-dropdown-item]")
    if (!item) return

    // Highlight follows the pointer (Radix-like): focus the hovered item so the
    // focus:bg-input/20 style applies.
    item.focus()

    // Items inside an open submenu: just highlight, keep the submenu open.
    if (item.closest("[data-dropdown-subcontent]")) return

    // Root-level hover: collapse any OTHER open submenu (so Theme/Help are never
    // both open), then open this one if it is a submenu trigger.
    const ownSub = item
      .closest("[data-dropdown-submenu]")
      ?.querySelector("[data-dropdown-subcontent]")
    this.submenuStack
      .filter((sub) => sub !== ownSub)
      .forEach((sub) => this.closeSubmenu(sub))
    if (item.hasAttribute("data-dropdown-subtrigger")) this.openSubmenu(item)
  }

  // Leaving the panel clears the item highlight (moves focus back to the panel).
  handleContentLeave() {
    if (this.open) this.contentEl.focus()
  }

  openSubmenu(trigger) {
    const wrapper = trigger.closest("[data-dropdown-submenu]")
    const sub = wrapper?.querySelector("[data-dropdown-subcontent]")
    if (!sub || this.submenuStack.includes(sub)) return
    sub.classList.remove("hidden")
    this.placePanel(sub, wrapper)
    sub.dataset.state = "open"
    trigger.setAttribute("aria-expanded", "true")
    trigger.dataset.state = "open"
    this.submenuStack.push(sub)
  }

  closeSubmenu(sub) {
    if (!sub) return
    sub.classList.add("hidden")
    sub.dataset.state = "closed"
    this.resetPanel(sub)
    const wrapper = sub.closest("[data-dropdown-submenu]")
    const trigger = wrapper?.querySelector("[data-dropdown-subtrigger]")
    trigger?.setAttribute("aria-expanded", "false")
    if (trigger) trigger.dataset.state = "closed"
    this.submenuStack = this.submenuStack.filter((s) => s !== sub)
  }

  closeAllSubmenus() {
    ;[...this.submenuStack].reverse().forEach((sub) => this.closeSubmenu(sub))
  }

  // === viewport collision ===

  // Keeps `panel` inside the viewport. `anchor` is the element the panel is
  // positioned against (the trigger for the root, the sub-trigger row for a
  // submenu), used to pick a flip side. Runs while the panel is visible but
  // before data-state=open, with the entrance animation suspended so its
  // zoom/slide transform is not measured; clearing it then starts the
  // animation from the corrected spot. A resize re-place skips that, so an
  // already open panel does not replay its entrance.
  placePanel(panel, anchor, { opening = true } = {}) {
    this.resetPanel(panel)
    if (opening) panel.style.animation = "none"

    const viewport = document.documentElement.clientWidth
    let rect = panel.getBoundingClientRect()

    const side = panel.dataset.side
    if (
      (side === "left" || side === "right") &&
      this.crossesEdge(rect, viewport)
    ) {
      const room = anchor.getBoundingClientRect()
      const space = { left: room.left, right: viewport - room.right }
      const other = side === "right" ? "left" : "right"
      if (space[other] > space[side]) {
        this.applySide(panel, other)
        rect = panel.getBoundingClientRect()
      }
    }

    // Shift whatever still crosses an edge flush against it, no gap (always a
    // top/bottom panel's way back in; for a side panel only when neither side
    // has room for it).
    let dx = 0
    if (rect.right > viewport) dx = viewport - rect.right
    if (rect.left + dx < 0) dx = -rect.left
    if (dx) {
      // Override, not add to, the class `translate` (-50% when centred), so
      // measure what that class contributes first.
      panel.style.translate = "none"
      const base = rect.left - panel.getBoundingClientRect().left
      panel.style.translate = `${base + dx}px 0`
    }

    panel.style.animation = ""
  }

  crossesEdge(rect, viewport) {
    return rect.left < 0 || rect.right > viewport
  }

  // Inline equivalent of the other side's `left-full ml-1` / `right-full mr-1`.
  applySide(panel, side) {
    const s = panel.style
    if (side === "left") {
      Object.assign(s, {
        left: "auto",
        right: "100%",
        marginLeft: "0",
        marginRight: "0.25rem",
      })
    } else {
      Object.assign(s, {
        left: "100%",
        right: "auto",
        marginLeft: "0.25rem",
        marginRight: "0",
      })
    }
    panel.dataset.baseSide ||= panel.dataset.side
    panel.dataset.side = side
  }

  resetPanel(panel) {
    const s = panel.style
    for (const prop of [
      "translate",
      "left",
      "right",
      "marginLeft",
      "marginRight",
      "animation",
    ]) {
      s[prop] = ""
    }
    if (panel.dataset.baseSide) {
      panel.dataset.side = panel.dataset.baseSide
      delete panel.dataset.baseSide
    }
  }

  // A submenu opened while the root panel is still zooming in (a hover right
  // after the click) is measured through that scale, and through its own
  // entrance too. Place everything again as each of those animations ends;
  // once none is running the measurement is exact. Same-task restyle, so no
  // visible jump when nothing changes.
  handleContentAnimationEnd(event) {
    if (!this.submenuStack.length) return
    if (
      event.target === this.contentEl ||
      this.submenuStack.includes(event.target)
    ) {
      this.reposition()
    }
  }

  // Window resize while open (e.g. a phone rotating): re-run the correction.
  reposition() {
    if (!this.open) return
    this.placePanel(this.contentEl, this.triggerTarget, { opening: false })
    this.submenuStack.forEach((sub) =>
      this.placePanel(sub, sub.closest("[data-dropdown-submenu]"), {
        opening: false,
      }),
    )
  }

  // === selection ===

  handleContentClick(event) {
    // Copy hooks (e.g. the account-menu build stamp) copy without closing.
    const copyEl = event.target.closest("[data-dropdown-copy]")
    if (copyEl) {
      event.preventDefault()
      navigator.clipboard?.writeText(copyEl.dataset.dropdownCopy || "")
      return
    }

    const item = event.target.closest("[data-dropdown-item]")
    if (!item || item.hasAttribute("disabled")) return

    if (item.hasAttribute("data-dropdown-checkbox")) {
      event.preventDefault()
      this.toggleCheckbox(item)
      return
    }
    if (item.hasAttribute("data-dropdown-radio")) {
      event.preventDefault()
      this.selectRadio(item)
      return
    }
    if (item.hasAttribute("data-dropdown-subtrigger")) {
      // Submenu triggers open on hover/keyboard; a click just keeps it open.
      return
    }
    // A plain item (link / button / logout form) selects and closes. Defer the
    // close to the next frame so the item's own default action (navigation /
    // form submit) fires first — closing synchronously here would swallow it.
    requestAnimationFrame(() => this.closeMenu({ focusTrigger: false }))
  }

  toggleCheckbox(item) {
    const checked = item.getAttribute("aria-checked") === "true"
    item.setAttribute("aria-checked", (!checked).toString())
    const indicator = item.querySelector(
      "[data-dropdown-indicator]",
    )?.firstElementChild
    indicator?.classList.toggle("hidden", checked)
    this.dispatch("checked", {
      detail: { checked: !checked, item },
      prefix: "dropdown-menu",
    })
  }

  selectRadio(item) {
    const name = item.dataset.radioName
    const group = item.closest("[data-dropdown-radio-group]")
    group?.querySelectorAll("[data-dropdown-radio]").forEach((radio) => {
      if (name && radio.dataset.radioName !== name) return
      const selected = radio === item
      radio.setAttribute("aria-checked", selected.toString())
      const indicator = radio.querySelector(
        "[data-dropdown-indicator]",
      )?.firstElementChild
      indicator?.classList.toggle("hidden", !selected)
    })
    this.dispatch("radio-change", {
      detail: { name, value: item.getAttribute("value") },
      prefix: "dropdown-menu",
    })
  }

  // === keyboard ===

  triggerKeydown(event) {
    if (["ArrowDown", "Enter", " "].includes(event.key)) {
      event.preventDefault()
      this.openMenu()
    }
  }

  handleContentKeydown(event) {
    const menu = this.activeMenu()
    const items = this.itemsIn(menu)
    const current = document.activeElement
    const index = items.indexOf(current)

    switch (event.key) {
      case "ArrowDown":
        event.preventDefault()
        this.focusItem(items[index < items.length - 1 ? index + 1 : 0])
        break
      case "ArrowUp":
        event.preventDefault()
        this.focusItem(items[index > 0 ? index - 1 : items.length - 1])
        break
      case "Home":
        event.preventDefault()
        this.focusItem(items[0])
        break
      case "End":
        event.preventDefault()
        this.focusItem(items[items.length - 1])
        break
      case "ArrowRight":
        if (current?.hasAttribute("data-dropdown-subtrigger")) {
          event.preventDefault()
          this.openSubmenu(current)
          this.focusItem(this.itemsIn(this.activeMenu())[0])
        }
        break
      case "ArrowLeft":
        if (this.submenuStack.length) {
          event.preventDefault()
          this.closeCurrentSubmenu()
        }
        break
      case "Escape":
        event.preventDefault()
        if (this.submenuStack.length) {
          this.closeCurrentSubmenu()
        } else {
          this.closeMenu()
        }
        break
      case "Tab":
        this.closeMenu({ focusTrigger: false })
        break
      case "Enter":
      case " ":
        if (current?.hasAttribute("data-dropdown-subtrigger")) {
          event.preventDefault()
          this.openSubmenu(current)
          this.focusItem(this.itemsIn(this.activeMenu())[0])
        }
        // Plain items: let the native click fire (handleContentClick closes).
        break
    }
  }

  closeCurrentSubmenu() {
    const sub = this.submenuStack[this.submenuStack.length - 1]
    const trigger = sub
      .closest("[data-dropdown-submenu]")
      ?.querySelector("[data-dropdown-subtrigger]")
    this.closeSubmenu(sub)
    this.focusItem(trigger)
  }

  // === helpers ===

  handleDocPointerdown(event) {
    if (
      this.element.contains(event.target) ||
      this.contentEl.contains(event.target)
    ) {
      return
    }
    this.closeMenu({ focusTrigger: false })
  }

  // The menu currently receiving keyboard focus: deepest open submenu, else root.
  activeMenu() {
    return this.submenuStack.length
      ? this.submenuStack[this.submenuStack.length - 1]
      : this.contentEl
  }

  // Direct items of a menu (excludes items nested in deeper sub-contents).
  itemsIn(menu) {
    if (!menu) return []
    return [...menu.querySelectorAll(ITEM_SELECTOR)].filter((el) => {
      const owner = el.closest("[data-dropdown-subcontent]") || this.contentEl
      return owner === menu
    })
  }

  focusItem(item) {
    item?.focus()
  }

  get triggerButton() {
    return (
      this.triggerTarget.querySelector("button, a, [tabindex]") ||
      this.triggerTarget
    )
  }
}
